import { Injectable } from '@angular/core';
import { AuthService } from '../../auth/services/auth.service';
import { PermissionService } from './permission-service.service';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment.dev';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Injectable({
  providedIn: 'root'
})
export class AgentWidgetService {
  private widgetIframe: HTMLIFrameElement | null = null;
  private widgetContainer: HTMLDivElement | null = null;
  private toggleButton: HTMLButtonElement | null = null;
  private isInitialized = false;
  private isExpanded = false;
  private nonce = '';
  private agentBaseUrl = environment.TE_AGENT_BASE_URL || 'http://localhost:3000';
  private allowedOrigin = new URL(this.agentBaseUrl).origin;
  private customContainer: HTMLElement | null = null;

  // Permission-related properties
  private telcoAgentPermissions: any[] = [];
  private hasAgentPermissions = false;

  // Store the complete permission response for future reference
  private telcoAgentPermissionData: any = null;

  // Widget state observable to allow components to react to widget state changes
  private widgetStateSubject = new BehaviorSubject<'hidden' | 'minimized' | 'expanded'>('hidden');
  public widgetState$ = this.widgetStateSubject.asObservable();

  // Track if we're on mobile view
  private isMobileSubject = new BehaviorSubject<boolean>(false);
  public isMobile$ = this.isMobileSubject.asObservable();

  // Track sidebar visibility in mobile view
  private mobileSidebarVisibleSubject = new BehaviorSubject<boolean>(false);
  public mobileSidebarVisible$ = this.mobileSidebarVisibleSubject.asObservable();

  // Track fullscreen state
  private isFullscreen = false;

  /**
   * Check if user has Telco_Agent permissions
   */
  public hasAgentPermission(): boolean {
    return this.hasAgentPermissions;
  }

  /**
   * Check if user has specific permission within Telco_Agent
   * @param permissionName - The name or display name of the permission to check
   */
  public hasSpecificPermission(permissionName: string): boolean {
    if (!this.hasAgentPermissions) return false;

    return this.telcoAgentPermissions.some(permission =>
      permission.Name?.toLowerCase() === permissionName.toLowerCase() ||
      permission.DisplayName?.toLowerCase() === permissionName.toLowerCase()
    );
  }

  /**
   * Get all Telco_Agent permissions
   */
  public getTelcoAgentPermissions(): any[] {
    return this.telcoAgentPermissions;
  }

  /**
   * Get the complete Telco_Agent permission data including resource types
   */
  public getTelcoAgentPermissionData(): any {
    return this.telcoAgentPermissionData;
  }

  /**
   * Check if user has view permission for the agent
   */
  public canViewAgent(): boolean {
    if (!this.hasAgentPermissions || !this.telcoAgentPermissions.length) return false;

    // Check if any resource has CanView permission set to 1
    return this.telcoAgentPermissions.some(permission =>
      permission.resource_permission?.[0]?.CanView === 1
    );
  }

  /**
   * Check if user has specific action permission (like CanCreate, CanEdit, CanDelete)
   * @param action - The action to check ('CanView', 'CanCreate', 'CanEdit', 'CanDelete')
   * @param permissionName - Optional specific permission name to check
   */
  public hasActionPermission(action: string, permissionName?: string): boolean {
    if (!this.hasAgentPermissions || !this.telcoAgentPermissions.length) return false;

    if (permissionName) {
      // Check specific permission
      const permission = this.telcoAgentPermissions.find(p =>
        p.Name?.toLowerCase() === permissionName.toLowerCase() ||
        p.DisplayName?.toLowerCase() === permissionName.toLowerCase()
      );
      return permission?.resource_permission?.[0]?.[action] === 1;
    } else {
      // Check if any permission has the action
      return this.telcoAgentPermissions.some(permission =>
        permission.resource_permission?.[0]?.[action] === 1
      );
    }
  }

  /**
   * Ensure the token is in the expected format (no extra quotes or escape characters)
   */
  private cleanToken(token: string): string {
    if (!token) return token;

    try {
      // If the token starts with quotes, it might be JSON stringified
      if (token.startsWith('"') || token.startsWith('\'')) {
        try {
          // Try to parse as JSON if it appears to be stringified
          return JSON.parse(token);
        } catch (e) {
          // If parsing fails, just continue with the cleaning
        }
      }

      // Remove any surrounding quotes and backslashes
      return token.replace(/^"|"$|\\|\\"/g, '');
    } catch (e) {
      console.error('Error cleaning token:', e);
      return token; // Return original token if cleaning fails
    }
  }

  constructor(
    private authService: AuthService,
    private permissionService: PermissionService,
    private breakpointObserver: BreakpointObserver
  ) {
    console.log("Hello world from AgentWidgetService");
    // Listen for messages from the iframe
    this.setupMessageListener();

    // Note: Removed automatic initialization from constructor
    // The widget will be initialized by the app component after proper authentication

    // Set up the breakpoint observer to detect mobile views
    this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      Breakpoints.TabletPortrait
    ]).subscribe(result => {
      this.isMobileSubject.next(result.matches);
      console.log('Mobile view detected:', result.matches);
    });
  }

  /**
   * Set a custom container for the widget (e.g., header element)
   * @param container The HTML element to contain the widget
   */
  public setContainer(container: HTMLElement): void {
    this.customContainer = container;
    console.log('Custom container set for the agent widget');

    // If already initialized, move the widget to the new container
    if (this.isInitialized && this.widgetContainer) {
      // Check if the widget is already in the DOM
      if (this.widgetContainer.parentElement) {
        this.widgetContainer.parentElement.removeChild(this.widgetContainer);
      }

      // Adjust positioning for the custom container
      this.widgetContainer.style.position = 'relative';
      this.widgetContainer.style.bottom = 'auto';
      this.widgetContainer.style.right = 'auto';

      // Add to the new container
      container.appendChild(this.widgetContainer);
      console.log('Widget moved to new container');
    }
  }

  /**
   * Initialize the widget if not already initialized and user has proper permissions
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!this.authService.isUserLoggedIn()) {
      console.warn('Cannot initialize AI agent widget - user not logged in');
      return;
    }

    // Check Telco_Agent permissions before initializing
    try {
      console.log("Checking Telco_Agent permissions...");
      const permissionResponse = await this.permissionService.getResourceCategoryById("Telco_Agent");
      console.log("Telco_Agent permissions response:", permissionResponse);

      // Check if response has the expected structure based on your API response format
      if (!permissionResponse ||
        !permissionResponse.resource_types ||
        permissionResponse.resource_types.length === 0 ||
        !permissionResponse.resource_types[0].resources) {
        console.warn('Cannot initialize AI agent widget - user does not have Telco_Agent permissions');
        console.log('Permission check failed. Response structure:', {
          hasResponse: !!permissionResponse,
          hasResourceTypes: !!permissionResponse?.resource_types,
          resourceTypesLength: permissionResponse?.resource_types?.length || 0,
          hasResources: !!permissionResponse?.resource_types?.[0]?.resources
        });
        return;
      }

      // Store the complete permission data
      this.telcoAgentPermissionData = permissionResponse;

      const telcoAgentPermissions = permissionResponse.resource_types[0].resources;
      console.log('Telco_Agent permissions found:', telcoAgentPermissions);

      // Store permissions for later use
      this.telcoAgentPermissions = telcoAgentPermissions;
      this.hasAgentPermissions = true;

      // Check if user has view permission for at least one resource
      const hasViewPermission = telcoAgentPermissions.some(permission =>
        permission.resource_permission?.[0]?.CanView === 1
      );

      if (!hasViewPermission) {
        console.warn('Cannot initialize AI agent widget - user does not have view permissions for Telco_Agent');
        console.log('Available permissions without view access:',
          telcoAgentPermissions.map(p => ({
            name: p.Name,
            displayName: p.DisplayName,
            canView: p.resource_permission?.[0]?.CanView
          }))
        );
        return;
      }

      console.log('User has valid Telco_Agent view permissions');

    } catch (error) {
      console.error('Error checking Telco_Agent permissions:', error);
      // You can choose to return here to prevent initialization, or continue with initialization
      // For now, we'll return to prevent initialization on permission check errors
      return;
    }

    this.createWidgetElements();
    this.setupToggleButton();
    this.appendWidgetToDOM();
    this.isInitialized = true;
    this.widgetStateSubject.next('minimized');
    console.log('Widget initialized successfully with proper permissions');

    // Generate a new nonce for secure communication
    this.nonce = this.generateNonce();
  }

  /**
   * Show the widget if it exists and the user is logged in
   */
  public showWidget(): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    if (this.widgetContainer) {
      this.widgetContainer.style.display = 'block';
      console.log('Widget container shown');
      this.widgetStateSubject.next('minimized');
    }
  }

  /**
   * Hide the widget if it exists
   * MODIFIED: Now only hides the expanded iframe but keeps the toggle button visible
   */
  public hideWidget(): void {
    if (this.widgetContainer) {
      // MODIFIED: Don't hide the entire container, just collapse it
      if (this.isExpanded) {
        this.minimizeWidget();
      }

      // MODIFIED: Set state to minimized instead of hidden to keep the chat icon visible
      this.widgetStateSubject.next('minimized');

      console.log('Widget minimized instead of hidden');
    }
  }

  /**
 * Expand the chat window as a drawer
 */
  public expandWidget(): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    if (this.widgetContainer && this.widgetIframe) {
      console.log('Expanding widget - responding to maximize request');

      // Find and hide the original widget if it exists
      const originalWidget = document.querySelector('.te-agent-widget') as HTMLElement;
      if (originalWidget && originalWidget !== this.widgetIframe) {
        originalWidget.style.display = 'none';
      }

      // Apply drawer-style positioning
      this.widgetContainer.classList.add('expanded');

      // Apply styles to the container to ensure it doesn't appear in the center
      this.widgetContainer.style.width = '0'; // Set width to 0 to prevent it taking space
      this.widgetContainer.style.height = '0';
      this.widgetContainer.style.overflow = 'visible'; // Allow the iframe to be visible outside

      // Position the iframe as a right-side drawer
      this.widgetIframe.style.display = 'block'; // Explicitly show iframe
      this.widgetIframe.style.position = 'fixed';
      this.widgetIframe.style.top = '0';
      this.widgetIframe.style.right = '0';
      this.widgetIframe.style.bottom = '0';
      this.widgetIframe.style.width = '400px'; // Fixed width for the drawer
      this.widgetIframe.style.height = '100vh'; // Full height
      this.widgetIframe.style.borderRadius = '0';
      this.widgetIframe.style.boxShadow = '-2px 0 10px rgba(0,0,0,0.1)';
      this.widgetIframe.style.border = 'none';
      this.widgetIframe.style.transition = 'transform 0.3s ease';
      this.widgetIframe.style.transform = 'translateX(0)'; // Slide in from right
      this.widgetIframe.style.zIndex = '99999';

      this.toggleButton.style.display = 'none'; // Explicitly hide button
      this.isExpanded = true;
      this.widgetStateSubject.next('expanded');

      // Send authentication data once iframe is loaded and expanded
      this.sendAuthData();

      // Send dimensions to the iframe
      this.setWidgetDimensions('400px', '100vh');

      // Check for stored company changes
      this.checkForStoredCompanyChanges().catch(error => {
        console.warn('Error restoring company state:', error);
      });

      console.log('Widget expanded as drawer, iframe shown');
    }
  }

  /**
   * Make the chat window fullscreen
   */
  public fullscreenWidget(): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    if (this.widgetContainer && this.widgetIframe) {
      console.log('Making widget fullscreen');

      // Apply fullscreen styles to container - leaving space for CRM header and sidebar
      this.widgetContainer.style.position = 'fixed';
      this.widgetContainer.style.top = '60px'; // Space for CRM header
      this.widgetContainer.style.left = '280px'; // Increased space for sidebar
      this.widgetContainer.style.right = '20px'; // Small margin from right edge
      this.widgetContainer.style.bottom = '20px'; // Small margin from bottom
      this.widgetContainer.style.width = 'calc(100vw - 300px)'; // Adjusted calculation
      this.widgetContainer.style.height = 'calc(100vh - 80px)'; // Full height minus header and margins
      this.widgetContainer.style.zIndex = '99999'; // Ensure it's above everything
      this.widgetContainer.style.background = 'white';
      this.widgetContainer.style.borderRadius = '8px'; // Add some border radius
      // this.widgetContainer.style.border = '1px solid #e5e7eb'; // Add subtle border

      // Apply fullscreen styles to iframe
      this.widgetIframe.style.width = '95%';
      this.widgetIframe.style.height = '100%';
      this.widgetIframe.style.borderRadius = '8px';
      this.widgetIframe.style.boxShadow = 'none';
      this.widgetIframe.style.display = 'block';

      // Hide toggle button
      this.toggleButton.style.display = 'none';

      // Set flags
      this.isFullscreen = true;
      this.isExpanded = true;
      this.widgetStateSubject.next('expanded');

      // Send authentication data
      this.sendAuthData();

      // Notify iframe about fullscreen dimensions (still 100vw/100vh for internal calculations)
      this.setWidgetDimensions('100vw', '100vh');

      console.log('Widget is now in windowed fullscreen mode');
    }
  }



  /**
  * Minimize the chat window
  */
  public minimizeWidget(): void {
    if (this.widgetContainer) {
      // Reset fullscreen styles if in fullscreen mode
      if (this.isFullscreen) {
        this.widgetContainer.style.position = 'fixed';
        this.widgetContainer.style.top = 'auto';
        this.widgetContainer.style.left = 'auto';
        this.widgetContainer.style.right = '20px';
        this.widgetContainer.style.bottom = '20px';
        this.widgetContainer.style.width = 'auto';
        this.widgetContainer.style.height = 'auto';
        this.widgetContainer.style.background = 'transparent';
        this.isFullscreen = false;
      }

      // Reset container styles that were set during expand
      this.widgetContainer.style.width = '';
      this.widgetContainer.style.height = '';
      this.widgetContainer.style.overflow = '';

      // Set position based on whether we're using a custom container
      if (this.customContainer) {
        // For custom container (header), keep it relative
        this.widgetContainer.style.position = 'relative';
        this.widgetContainer.style.bottom = 'auto';
        this.widgetContainer.style.right = 'auto';
      } else {
        // For default bottom-right positioning
        this.widgetContainer.style.position = 'fixed';
        this.widgetContainer.style.bottom = '20px';
        this.widgetContainer.style.right = '20px';
      }

      // If the iframe is visible, animate it out
      if (this.widgetIframe && this.widgetIframe.style.display !== 'none') {
        // Start slide-out animation
        this.widgetIframe.style.transform = 'translateX(100%)';

        // Wait for animation to complete before fully hiding
        setTimeout(() => {
          if (this.widgetIframe) {
            this.widgetIframe.style.display = 'none';
            // Reset iframe styles
            this.widgetIframe.style.transform = 'translateX(100%)'; // Ready for next slide-in
            this.widgetIframe.style.width = '380px';
            this.widgetIframe.style.height = '660px';
            this.widgetIframe.style.borderRadius = '10px';
            this.widgetIframe.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
          }

          // Make sure no original widget is visible
          const originalWidget = document.querySelector('.te-agent-widget') as HTMLElement;
          if (originalWidget && originalWidget !== this.widgetIframe) {
            originalWidget.style.display = 'none';
          }

          if (this.toggleButton) {
            this.toggleButton.style.display = 'block'; // Explicitly show button
          }
        }, 300); // Match the transition duration
      } else {
        // If iframe was already hidden, just make sure the button is visible
        if (this.toggleButton) {
          this.toggleButton.style.display = 'block';
        }
      }

      this.widgetContainer.classList.remove('expanded');

      // Hide the hamburger menu button
      const hamburgerButton = this.widgetContainer.querySelector('.te-agent-mobile-sidebar-toggle');
      if (hamburgerButton instanceof HTMLElement) {
        hamburgerButton.style.display = 'none';
      }

      // Also close the sidebar if it's open
      this.mobileSidebarVisibleSubject.next(false);

      this.isExpanded = false;
      this.widgetStateSubject.next('minimized');

      console.log('Widget minimized');
    }
  }

  /**
   * Toggle between expanded and minimized states
   */
  public toggleWidget(): void {
    console.log('Toggle widget called, current expanded state:', this.isExpanded);
    if (this.isExpanded) {
      this.minimizeWidget();
    } else {
      this.expandWidget();
    }
  }

  /**
   * Clean up widget resources when component is destroyed
   */
  public disposeWidget(): void {
    if (this.widgetContainer && document.body.contains(this.widgetContainer)) {
      document.body.removeChild(this.widgetContainer);
    }

    this.widgetIframe = null;
    this.widgetContainer = null;
    this.toggleButton = null;
    this.isInitialized = false;
    this.isExpanded = false;
    this.widgetStateSubject.next('hidden');
  }

  /**
   * Helper method to get the base URL for the current company
   * ADDED: New helper method to centralize base URL logic
   */
  private getBaseUrlForCompany(company?: string): string | null {
    const currentCompany = company || localStorage.getItem('currentCompany') || 'unity';

    // 1. Try getting from api_base_url (set by embedded auth)
    let baseUrl = localStorage.getItem('api_base_url');

    // 2. If not found, try getting from BaseUrl (set by company change)
    if (!baseUrl) {
      baseUrl = localStorage.getItem('BaseUrl');
    }

    // 3. If still not found, derive from Applications data
    if (!baseUrl) {
      try {
        const applicationsData = localStorage.getItem('Applications');
        if (applicationsData) {
          const applications = JSON.parse(applicationsData);
          const currentApp = applications.find(app => app.name === currentCompany);
          if (currentApp && currentApp.BaseUrl) {
            baseUrl = currentApp.BaseUrl;
          }
        }
      } catch (e) {
        console.warn('Could not parse Applications data:', e);
      }
    }

    // 4. Fallback to environment values based on company
    if (!baseUrl) {
      if (currentCompany === 'mingle') {
        baseUrl = environment.Prepaid_Mingle_API || 'https://mingle-stg-api.aqsatel.com/';
      } else {
        baseUrl = environment.BASE_API || 'https://manage-api.aqsatel.com/';
      }
    }

    console.log(`Base URL for company '${currentCompany}': ${baseUrl}`);
    return baseUrl;
  }

  /**
   * Send current authentication data to the widget
   * UPDATED: Now includes base URL in initial auth data
   */
  private async sendAuthData(): Promise<void> {
    if (!this.widgetIframe || !this.widgetIframe.contentWindow) return;

    try {
      // Get the current company
      const currentCompany = localStorage.getItem('currentCompany') || 'unity';

      // Get tokens for both company contexts if available
      const authData: any = {
        nonce: this.nonce,
        timestamp: Date.now(),
        currentCompany: currentCompany
      };

      try {
        const unityAuth = await this.authService.getCompanyTokens('unity');
        if (unityAuth) {
          authData.unityToken = this.cleanToken(unityAuth.token);
          authData.unityUserId = unityAuth.userId;
        }
      } catch (e) {
        console.warn('Could not retrieve Unity auth data');
      }

      try {
        const mingleAuth = await this.authService.getCompanyTokens('mingle');
        if (mingleAuth) {
          authData.mingleToken = this.cleanToken(mingleAuth.token);
          authData.mingleUserId = mingleAuth.userId;
        }
      } catch (e) {
        console.warn('Could not retrieve Mingle auth data');
      }

      // Add user details if available
      const userDetails = this.authService.userDetails;
      if (userDetails) {
        authData.user = {
          id: userDetails.userID,
          name: userDetails.name,
          email: userDetails.email,
          role: userDetails.role
        };
      }

      // IMPORTANT: Get the base URL for the current company
      const baseUrl = this.getBaseUrlForCompany(currentCompany);
      if (baseUrl) {
        authData.baseUrl = baseUrl;
        console.log(`Sending initial auth data with base URL: ${baseUrl} for company: ${currentCompany}`);
      } else {
        console.warn(`Could not determine base URL for company: ${currentCompany}`);
      }

      // Send the auth data to the iframe
      this.widgetIframe.contentWindow.postMessage({
        type: 'AGENT_AUTH_DATA',
        data: authData
      }, this.allowedOrigin);

      console.log('Sent initial auth data with base URL to agent widget');

    } catch (error) {
      console.error('Error sending auth data to widget:', error);
    }
  }

  /**
   * Send dimensions to the widget iframe
   */
  public setWidgetDimensions(width: string, height: string): void {
    if (!this.widgetIframe || !this.widgetIframe.contentWindow) {
      console.warn('Cannot set dimensions - widget not initialized');
      return;
    }

    console.log(`Setting agent dimensions: ${width} × ${height}`);

    // Send message to agent iframe
    this.widgetIframe.contentWindow.postMessage({
      type: 'SET_DIMENSIONS',
      data: { width, height }
    }, this.allowedOrigin);
  }

  /**
   * Setup message listener for communication with the iframe
   */
  private setupMessageListener(): void {
    // Regular RxJS message handler with origin filter
    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter(event => event.origin === this.allowedOrigin)
      )
      .subscribe(event => {
        this.handleWidgetMessage(event.data);
      });
  }

  /**
   * Handle incoming messages from the widget iframe
   */
  private handleWidgetMessage(message: any): void {
    if (!message || !message.type) return;
    console.log("Handling message from widget:", message);
    switch (message.type) {
      case 'AGENT_READY':
        // Widget is ready, send auth data
        this.sendAuthData();
        break;

      case 'AGENT_AUTH_EXPIRED':
        // Authentication expired, request refresh
        this.handleAuthExpired();
        break;

      case 'AGENT_TOGGLE':
        // Widget wants to toggle its state
        this.toggleWidget();
        break;

      case 'MAXIMIZE_AGENT':
        // Widget wants to be maximized (only if not in fullscreen mode)
        if (!this.isFullscreen) {
          this.expandWidget();
          console.log('Widget maximized via maximize button');
        } else {
          console.log('Ignoring MAXIMIZE_AGENT - already in fullscreen mode');
        }
        break;

      case 'FULLSCREEN_AGENT':
        // Widget wants to be fullscreen
        this.fullscreenWidget();
        console.log('Widget set to fullscreen via fullscreen button');
        break;

      case 'AGENT_ACTION':
        // Widget wants to trigger an action in the CRM
        this.handleAgentAction(message.data);
        break;

      case 'CLOSE_AGENT':
      case 'MINIMIZE_AGENT':
        // Widget wants to be minimized (handles both old and new message types)
        console.log(`Minimizing widget via ${message.type} message`);
        this.minimizeWidget();
        break;
    }
  }

  /**
   * Handle expired authentication by requesting a token refresh
   */
  private handleAuthExpired(): void {
    if (this.authService.isTokenExpired()) {
      // Show token expired dialog if needed
      this.authService.showTokenExpiredDialog().then(confirmed => {
        if (confirmed) {
          // User has logged in again, re-send auth data
          this.sendAuthData();
        }
      });
    } else if (this.authService.isRefreshTokenAboutToExpire()) {
      // Refresh the token
      this.authService.showRefreshTokenDialog().then(confirmed => {
        if (confirmed) {
          // Token refreshed, re-send auth data
          setTimeout(() => this.sendAuthData(), 1000); // Give time for the refresh to complete
        }
      });
    }
  }

  /**
   * Notify the agent about company change
   * @param companyData Information about the new company
   */
  public async notifyCompanyChange(companyData: { currentCompany: string; token?: string; userId?: number; baseUrl: string }): Promise<void> {
    if (!this.widgetIframe || !this.widgetIframe.contentWindow) {
      console.warn('Cannot notify agent about company change - widget not initialized');
      return;
    }

    // IMPORTANT: Always get fresh token data for the requested company
    // This ensures we use the correct token for the company being switched to
    try {
      console.log(`Getting fresh token for company: ${companyData.currentCompany}`);
      const tokenData = await this.authService.getCompanyTokens(companyData.currentCompany);

      if (!tokenData || !tokenData.token) {
        console.error(`Failed to get token for company: ${companyData.currentCompany}`);
        return;
      }

      // Make company data available for debugging in browser console
      if (typeof window !== 'undefined') {
        (window as any).lastCompanyChange = {
          company: companyData.currentCompany,
          tokenHint: tokenData.token ? tokenData.token.substring(0, 10) + '...' : null,
          time: new Date().toISOString()
        };
        console.log('Stored company change info for debugging in window.lastCompanyChange');
      }

      // Use the token we just retrieved rather than the one passed in
      const token = tokenData.token;
      const userId = tokenData.userId;

      // Create a message with company change data
      const message: {
        type: string;
        data: {
          nonce: string;
          timestamp: number;
          currentCompany: string;
          company: string; // Added explicit company field
          baseUrl: string;
          unityToken?: string;
          unityUserId?: number;
          mingleToken?: string;
          mingleUserId?: number;
        }
      } = {
        type: 'COMPANY_CHANGED',
        data: {
          nonce: this.nonce,
          timestamp: Date.now(),
          currentCompany: companyData.currentCompany,
          company: companyData.currentCompany, // Add explicit company field
          baseUrl: companyData.baseUrl
        }
      };

      // Add token based on company
      const isUnity = companyData.currentCompany === 'unity';
      const isMingle = companyData.currentCompany === 'mingle';

      // Clean token to remove any problematic formatting
      const cleanedToken = this.cleanToken(token);

      if (isUnity) {
        message.data.unityToken = cleanedToken;
        message.data.unityUserId = userId;
        // Clear any mingle token to avoid confusion
        message.data.mingleToken = undefined;
        message.data.mingleUserId = undefined;
        console.log('Setting Unity token for company change');
      } else if (isMingle) {
        message.data.mingleToken = cleanedToken;
        message.data.mingleUserId = userId;
        // Clear any unity token to avoid confusion
        message.data.unityToken = undefined;
        message.data.unityUserId = undefined;
        console.log('Setting Mingle token for company change');
      } else {
        console.warn(`Unknown company: ${companyData.currentCompany}`);
        return; // Exit if company is unknown
      }

      // Log full details of what's being sent (for debugging)
      console.log('Sending company change notification with full details:', {
        company: companyData.currentCompany,
        token: cleanedToken ? cleanedToken.substring(0, 15) + '...' : null,
        userId: userId,
        baseUrl: companyData.baseUrl,
        message: {
          dataType: message.type,
          company: message.data.company,
          currentCompany: message.data.currentCompany,
          hasUnityToken: !!message.data.unityToken,
          hasMingleToken: !!message.data.mingleToken
        }
      });

      // Send message to agent iframe
      console.log('Notifying agent about company change:', { company: companyData.currentCompany, baseUrl: companyData.baseUrl });
      this.widgetIframe.contentWindow.postMessage(message, this.allowedOrigin);

      // Save last successful company change information for recovery purposes
      try {
        sessionStorage.setItem('last_company_change', JSON.stringify({
          company: companyData.currentCompany,
          userId: userId,
          baseUrl: companyData.baseUrl,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Could not save company change info to session storage', e);
      }
    } catch (error) {
      console.error('Error sending company change notification to agent:', error);
    }
  }

  /**
   * Handle actions requested by the agent
   */
  private handleAgentAction(actionData: any): void {
    if (!actionData || !actionData.action) return;

    // Implement action handling based on your CRM's needs
    console.log('Agent requested action:', actionData);

    // Example: Navigate to a specific page in the CRM
    // if (actionData.action === 'NAVIGATE' && actionData.path) {
    //   this.router.navigateByUrl(actionData.path);
    // }
  }

  /**
   * Toggle the sidebar visibility in mobile view
   * NEW METHOD: Add this to control mobile sidebar visibility
   */
  public toggleMobileSidebar(): void {
    const currentValue = this.mobileSidebarVisibleSubject.getValue();
    this.mobileSidebarVisibleSubject.next(!currentValue);
    console.log('Mobile sidebar toggled:', !currentValue);
  }

  /**
   * Create the widget DOM elements
   * MODIFIED: Updated to add mobile sidebar toggle button
   */
  private createWidgetElements(): void {
    // Create container
    this.widgetContainer = document.createElement('div');
    this.widgetContainer.className = 'te-agent-widget-container';

    // Set position based on whether we're using a custom container
    if (this.customContainer) {
      this.widgetContainer.style.position = 'relative';
      // Don't set bottom/right if in a custom container
    } else {
      this.widgetContainer.style.position = 'fixed';
      this.widgetContainer.style.bottom = '20px';
      this.widgetContainer.style.right = '20px';
    }

    this.widgetContainer.style.zIndex = '10000'; // Higher z-index to ensure visibility
    this.widgetContainer.style.display = 'none'; // Hidden by default
    this.widgetContainer.style.border = '0 !important'; // Hidden by default

    // Create toggle button
    this.toggleButton = document.createElement('button');
    this.toggleButton.className = 'te-agent-widget-toggle';

    // Determine if we're in the header (custom container) or not
    const isInHeader = !!this.customContainer;

    // Create a chat icon with different sizes and styles based on location
    const iconSize = isInHeader ? '40px' : '60px';
    const iconPadding = isInHeader ? '0 10px' : '0';
    // Updated with blue background
    const iconStyles = isInHeader ?
      // Styles for header placement
      `
    width: ${iconSize};
    height: ${iconSize};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.10);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    border: none !important;
    outline: none !important;
      margin: 0 5px;
    ` :
      // Original styles for bottom-right placement
      `
    width: ${iconSize};
    height: ${iconSize};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 20px rgba(37, 99, 235, 0.4);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    border: none !important;
      outline: none !important;
      `;

    this.toggleButton.innerHTML = `
      <div style="${iconStyles}" class="chat-icon-wrapper">
        <img src="assets/images/svg-icon/ai.gif" alt="AI Icon" style="width:25px;height:25px;" />
        
      </div>
      <style>
        .te-agent-widget-toggle {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
        .te-agent-widget-toggle:focus {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
        .te-agent-widget-toggle:active {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
        .chat-icon-wrapper {
          border: none !important;
          outline: none !important;
        }
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      </style>
    `;

    // Button styling - ensuring no borders in any state
    this.toggleButton.style.background = 'transparent';
    this.toggleButton.style.border = 'none';
    this.toggleButton.style.cursor = 'pointer';
    this.toggleButton.style.display = 'block';
    this.toggleButton.style.padding = '0';
    this.toggleButton.style.outline = 'none';
    this.toggleButton.style.boxShadow = 'none';
    this.toggleButton.style.borderRadius = '0';

    // Ensure no border on focus or any other state
    this.toggleButton.addEventListener('focus', () => {
      this.toggleButton.style.border = 'none';
      this.toggleButton.style.outline = 'none';
      this.toggleButton.style.boxShadow = 'none';
    });

    this.toggleButton.addEventListener('blur', () => {
      this.toggleButton.style.border = 'none';
      this.toggleButton.style.outline = 'none';
      this.toggleButton.style.boxShadow = 'none';
    });

    // Add hover effects
    this.toggleButton.addEventListener('mouseenter', () => {
      const wrapper = this.toggleButton.querySelector('.chat-icon-wrapper') as HTMLElement;
      if (wrapper) {
        wrapper.style.transform = 'scale(1.1)';
        wrapper.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.25)';
      }
    });

    this.toggleButton.addEventListener('mouseleave', () => {
      const wrapper = this.toggleButton.querySelector('.chat-icon-wrapper') as HTMLElement;
      if (wrapper) {
        wrapper.style.transform = 'scale(1)';
        wrapper.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
      }
    });

    // Create iframe with larger dimensions
    this.widgetIframe = document.createElement('iframe');
    this.widgetIframe.className = 'te-agent-widget-iframe';
    this.widgetIframe.src = `${this.agentBaseUrl}?embedded=true`;
    this.widgetIframe.style.border = 'none';
    this.widgetIframe.style.width = '380px'; // Increased from 350px
    this.widgetIframe.style.height = '660px'; // Increased from 500px
    this.widgetIframe.style.borderRadius = '10px';
    this.widgetIframe.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)'; // Enhanced shadow
    this.widgetIframe.style.display = 'none'; // Hidden by default


    // NEW: Create sidebar container for mobile view

  }

  /**
   * Setup toggle button event handlers
   */
  private setupToggleButton(): void {
    if (!this.toggleButton) return;

    this.toggleButton.addEventListener('click', (event) => {
      console.log('Toggle button clicked');
      event.preventDefault();
      event.stopPropagation();
      this.toggleWidget();
    });

    console.log('Toggle button event listener attached');
  }

  /**
   * Append widget elements to the DOM
   */
  private appendWidgetToDOM(): void {
    if (!this.widgetContainer || !this.toggleButton || !this.widgetIframe) return;

    this.widgetContainer.appendChild(this.toggleButton);
    this.widgetContainer.appendChild(this.widgetIframe);

    // Use the custom container if available, otherwise default to document.body
    const targetContainer = this.customContainer || document.body;
    targetContainer.appendChild(this.widgetContainer);

    // If we're using a custom container, adjust the positioning
    if (this.customContainer) {
      this.widgetContainer.style.position = 'relative';
      this.widgetContainer.style.bottom = 'auto';
      this.widgetContainer.style.right = 'auto';
    }
  }

  /**
   * Generate a random nonce for secure communication
   */
  private generateNonce(): string {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  }

  /**
   * Check for stored company change information and restore if needed
   * This helps recover state after page reloads
   */
  private async checkForStoredCompanyChanges(): Promise<void> {
    try {
      const storedInfo = sessionStorage.getItem('last_company_change');
      if (!storedInfo) {
        return;
      }

      const parsedInfo = JSON.parse(storedInfo);
      const now = Date.now();
      const age = now - parsedInfo.timestamp;

      // Only use stored information if less than 1 hour old
      if (age > 3600000) {
        console.log('Stored company change info is too old, discarding');
        sessionStorage.removeItem('last_company_change');
        return;
      }

      console.log('Found stored company change info:', parsedInfo);
      console.log('Restoring company state from stored information');

      // Call notifyCompanyChange with just the company and base URL
      // The method will now fetch the correct token for this company
      await this.notifyCompanyChange({
        currentCompany: parsedInfo.company,
        baseUrl: parsedInfo.baseUrl
      });

    } catch (error) {
      console.warn('Error checking for stored company changes:', error);
    }
  }

  /**
   * Debug helper: Reset the widget iframe src
   */
  public resetWidgetIframe(): void {
    if (this.widgetIframe) {
      console.log('Resetting widget iframe');
      // Store the original src
      const originalSrc = this.widgetIframe.src;

      // Set a timeout to restore the src after a brief delay
      setTimeout(() => {
        if (this.widgetIframe) {
          this.widgetIframe.src = originalSrc;
          console.log('Widget iframe src restored');
        }
      }, 100);

      // Clear the src to force reload
      this.widgetIframe.src = '';
    }
  }

  /**
   * Debug helper: Manually trigger company change
   * @param company The company to change to ('unity' or 'mingle')
   */
  public async debugCompanyChange(company: 'unity' | 'mingle'): Promise<void> {
    console.log(`Debug: Manual company change to ${company}`);

    try {
      const baseUrl = company === 'unity' ? 'https://manage-api.aqsatel.com/' : 'https://mingle-stg-api.aqsatel.com/';

      await this.notifyCompanyChange({
        currentCompany: company,
        baseUrl: baseUrl
      });

      console.log(`Debug: Manual company change to ${company} complete`);
    } catch (error) {
      console.error(`Debug: Manual company change to ${company} failed:`, error);
    }
  }
}
